import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { createSearchParams, useNavigate } from "react-router-dom";
import Moment from "react-moment";
import { NumericFormat } from "react-number-format";
import FullHeight from "../../../shared/components/FullHeight";
import Loader from "../../../shared/components/Loader";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import SortTableHead, { TableColumnDefinition } from "../../../shared/components/SortTableHead";
import Table from "../../../shared/components/tables/Table";
import TableEmpty from "../../../shared/components/tables/TableEmpty";
import TablePagination from "../../../shared/components/tables/TablePagination";
import BoxSearchIcon from "../../../shared/assets/icons/BoxSearchIcon";
import ActionToast from "../../../shared/components/ActionToast";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { NotificationMessage, getName } from "../../../shared/models/models";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import PresetChip from "../../../shared/components/PresetChip";
import { productCondition } from "../../../common/models/CommonModels";
import {
  CustomerMyRfqsState,
  Rfq,
  RfqQueryParameter,
  RfqStatusCode,
  RfqsQuery,
  isRfqsQueryEmpty,
  urgencyStatus
} from "../models/models";
import {
  changeRfqQuery,
  closeRfq,
  getCategories,
  getManufacturers,
  getRfqs,
  setError,
  setSuccess
} from "../customerMyRfqsSlice";
import OffersMathesCount from "./OffersMathesCount";
import MyOffers from "./myOffers/MyOffers";
import { fulfillmentPresets, matchStatusCodes, visibilityPresets } from "../shared/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";

const MyRfqs = () => {
  const { query, rfqs, rfq, loading, success, error } = useAppSelector<CustomerMyRfqsState>(
    (state: any) => state.customerMyRfqs
  );

  const columns: TableColumnDefinition[] = [
    { title: "Product details", parameter: RfqQueryParameter.productNumber },
    { title: "Condition" },
    { title: "Target price, quantity", twoLevelsColumWidth: "200px" },
    { title: "Urgency", parameter: RfqQueryParameter.urgency },
    { title: "RFQ details" },
    { title: "Offers & matches" },
    { title: undefined }
  ];

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate } = useTranslation("common");

  const handleGoToCreate = () => {
    navigate("/trading/my-rfqs/new");
  };

  const [isOpenOffers, setIsOpenOffers] = useState(false);
  const [rfqId, setRfqId] = useState("");

  const handleOpenOffers = (rfqId: string) => {
    setIsOpenOffers(true);
    setRfqId(rfqId);
  };

  const handleOpenMathes = (item: Rfq) => {
    navigate({
      pathname: "/search",
      search: createSearchParams({
        manufacturer: item.manufacturer.name,
        productNumber: item.productNumber
      }).toString()
    });
  };

  const handleGoToMyRfqs = () => {
    setIsOpenOffers(false);
  };

  const handleVisibilityPresetChange = (event: SelectChangeEvent) => {
    const visibilityPresetValue = Number(event.target.value);
    const selectedVisibilityPreset = visibilityPresets.find(({ value }) => visibilityPresetValue === value);

    if (selectedVisibilityPreset) {
      const newQuery = { ...query, ...selectedVisibilityPreset.queryParams };
      dispatch(changeRfqQuery(newQuery));
    }
  };

  const handleFulfillmentPresetChange = (queryParams: Partial<RfqsQuery>) => {
    const newQuery = { ...query, ...queryParams };
    dispatch(changeRfqQuery(newQuery));
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeRfqQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size };
    await dispatch(changeRfqQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no };
    await dispatch(changeRfqQuery(newQuery));
    dispatch(getRfqs(newQuery));
  };

  const handleCloseRfq = async (item: Rfq) => {
    if (item) {
      await dispatch(closeRfq(item.id));
      await dispatch(getRfqs(query));
    }
  };

  const CreateRfqButton = () => {
    return (
      <Button variant="contained" onClick={handleGoToCreate}>
        Create new RFQ
      </Button>
    );
  };

  const isQueryEmpty = isRfqsQueryEmpty(query);

  useEffect(() => {
    dispatch(getManufacturers());
    dispatch(getCategories());
    handleVisibilityPresetChange({ target: { value: visibilityPresets[0].value } } as unknown as SelectChangeEvent);
    handleFulfillmentPresetChange(fulfillmentPresets[0].queryParams);
  }, []);

  useEffect(() => {
    dispatch(getRfqs(query));
  }, [query]);

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9"
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            ".MuiOutlinedInput-input.MuiSelect-select": {
              color: dt.blue,
              fontSize: "20px",
              fontWeight: "500",
              minHeight: "auto"
            },

            ".MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Box className="flex" sx={{ height: "calc(100% - 75px)" }}>
        {!isOpenOffers && (
          <Box className="flex flex-col w-full">
            <Toolbar className="flex items-center justify-between">
              <Box className="flex items-center">
                <Typography fontSize={24} fontWeight={500} className="heading-2">
                  My RFQs
                </Typography>

                <Select
                  className="ml-2"
                  displayEmpty
                  defaultValue={query.visibilityCode.toString()}
                  value={query.visibilityCode.toString()}
                  onChange={handleVisibilityPresetChange}
                >
                  {visibilityPresets.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value} disabled={preset.disabled}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>

                <Box className="ml-16 flex gap-4">
                  {fulfillmentPresets.map((preset) => (
                    <PresetChip
                      key={preset.value}
                      label={preset.label}
                      active={matchStatusCodes(query.statusCodes, preset.queryParams.statusCodes)}
                      onClick={() => handleFulfillmentPresetChange(preset.queryParams)}
                    />
                  ))}
                </Box>
              </Box>

              <CreateRfqButton />
            </Toolbar>

            <Box className="px-4 v-fill">
              {loading ? (
                <FullHeight verticalOffset="medium">
                  <Loader />
                </FullHeight>
              ) : !rfqs?.items.length ? (
                <TableEmpty
                  iconComponent={BoxSearchIcon}
                  text={isQueryEmpty ? "You have no RFQs" : "You don’t have such RFQs"}
                  secondaryText={isQueryEmpty ? "Your first RFQ will be shown here" : undefined}
                >
                  {isQueryEmpty && <CreateRfqButton />}
                </TableEmpty>
              ) : (
                <Table>
                  <SortTableHead
                    columns={columns}
                    sortBy={query.sortBy}
                    sortDesc={query.sortDesc}
                    onSort={handleSort}
                  />

                  <TableBody>
                    {rfqs?.items.map((item) => (
                      <TableRow key={item.id} selected={item.id === rfq?.id}>
                        <TableCell sx={{ width: "25%" }}>
                          <Box className="block">
                            <Box className="text-14-medium" color={"#333333"} sx={{ wordBreak: "break-all" }}>
                              {item.productNumber}
                            </Box>

                            <Box className="mt-2">
                              <Box className="text-12-medium" color={"#333333"}>
                                {item.manufacturer.name}
                              </Box>
                              <Box className="text-12-regular" color={"#747881"}>
                                {item.category.name}
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="text-14-medium" color={"#333333"}>
                            {item.productConditionCodes?.map((x) => translate(`productCondition.${productCondition(x)}`)).join(", ")}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="text-18-medium" color={"#333333"}>
                            <NumericFormat
                              value={item.maxUnitPrice?.value}
                              displayType="text"
                              thousandSeparator=" "
                              suffix="€"
                            />
                          </Box>
                          <Box className="text-14-medium" color={"#333333"}>
                            {item.quantity + "\u00A0" + "pcs"}
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box className="text-14-medium" color={"#747881"}>
                            {urgencyStatus(item.urgencyCode)}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ minWidth: "200px" }}>
                          <Box>
                            <Box className="text-12-regular" color={"#747881"}>
                              Created on{"\u00A0"}
                              <Moment date={item.createdDate} format="DD.MM.YYYY" />
                            </Box>

                            <Box className="text-12-regular" color={"#747881"}>
                              {"#" + item.number}
                            </Box>

                            <Box className="text-12-medium" color={"#333333"}>
                              Last call on{"\u00A0"}
                              <Moment date={item.lastCall} format="DD.MM.YYYY" />
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <OffersMathesCount
                            count={item.offersCount}
                            openOffers={() => handleOpenOffers(item.id)}
                            text="offers"
                          />
                          <OffersMathesCount
                            count={item.matchesCount}
                            text="matches"
                            item={item}
                            openMatches={() => handleOpenMathes(item)}
                          />
                        </TableCell>

                        <TableCell>
                          {item.statusCode === RfqStatusCode.open && (
                            <TableActionButton stopPropagation={true}>
                              <MenuItem
                                onClick={() =>
                                  confirm({
                                    title: "Close RFQ",
                                    text: `Are you sure you want to close ${getName(
                                      item.manufacturer.name,
                                      item.productNumber
                                    )} RFQ?`,
                                    confirmButton: "Yes, close",
                                    closeButton: "No",
                                    severity: "error",
                                    onConfirm: () => handleCloseRfq(item)
                                  })
                                }
                              >
                                <DeleteOutlinedIcon />
                                Close RFQ
                              </MenuItem>
                            </TableActionButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>

            <TablePagination
              variant="bottom"
              pageNo={query.pageNo}
              pageSize={query.pageSize}
              totalCount={rfqs?.totalCount}
              onPageNoChange={handlePageNoChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}

        {isOpenOffers && <MyOffers openOffers={handleOpenOffers} closeOffers={handleGoToMyRfqs} rfqId={rfqId} />}
      </Box>
    </ThemeProvider>
  );
};

export default MyRfqs;
